import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Phone, PhoneCall, PhoneOff, Mic, MicOff, Volume2, VolumeX, User, Clock, FileText, Send, Plus, Search, History, Users, Shield, AlertTriangle, Lock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CallCenterDialer() {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isOnCall, setIsOnCall] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isHold, setIsHold] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [recentCalls, setRecentCalls] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [scripts, setScripts] = useState([]);
  const [selectedScript, setSelectedScript] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [notes, setNotes] = useState('');
  const [showScriptDialog, setShowScriptDialog] = useState(false);
  const [queues, setQueues] = useState([]);
  const [selectedQueue, setSelectedQueue] = useState('');
  const [callCenterMode, setCallCenterMode] = useState('safe'); // safe or active
  const [loading, setLoading] = useState(true);

  // Fetch call center mode
  useEffect(() => {
    const fetchMode = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const res = await axios.get(`${API}/settings/call-center`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCallCenterMode(res.data.mode || 'safe');
      } catch (err) {
        console.error('Failed to fetch call center mode');
      }
    };
    fetchMode();
  }, []);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('access_token');
        const headers = { Authorization: `Bearer ${token}` };
        
        const [interactionsRes, contactsRes, scriptsRes, queuesRes] = await Promise.all([
          axios.get(`${API}/call-center/interactions?page_size=20&interaction_type=outbound_call`, { headers }),
          axios.get(`${API}/contacts?page_size=50`, { headers }),
          axios.get(`${API}/call-center/scripts?script_type=outbound`, { headers }),
          axios.get(`${API}/call-center/queues`, { headers })
        ]);
        
        setRecentCalls(interactionsRes.data.interactions || []);
        setContacts(contactsRes.data.contacts || []);
        setScripts(scriptsRes.data.scripts || []);
        setQueues(queuesRes.data.queues || []);
      } catch (err) {
        console.error('Failed to fetch data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Call timer
  useEffect(() => {
    let interval;
    if (isOnCall) {
      interval = setInterval(() => {
        setCallDuration(d => d + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isOnCall]);

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleDial = (digit) => {
    if (!isOnCall) {
      setPhoneNumber(prev => prev + digit);
    }
  };

  const handleCall = async () => {
    if (!phoneNumber) {
      toast.error('Please enter a phone number');
      return;
    }
    
    // In production, this would integrate with CallRail/telephony provider
    toast.info('Initiating call via CallRail...');
    setIsOnCall(true);
    setCallDuration(0);
    
    // Log the outbound call attempt
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${API}/call-center/interactions`, {
        interaction_type: 'outbound_call',
        direction: 'outbound',
        caller_number: phoneNumber,
        contact_id: selectedContact?.id,
        contact_name: selectedContact?.first_name ? `${selectedContact.first_name} ${selectedContact.last_name}` : null,
        queue_id: selectedQueue || null,
        status: 'in_progress',
        notes: notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {
      console.error('Failed to log call:', err);
    }
  };

  const handleHangup = () => {
    setIsOnCall(false);
    setCallDuration(0);
    setIsMuted(false);
    setIsHold(false);
    toast.success(`Call ended - Duration: ${formatDuration(callDuration)}`);
  };

  const selectContact = (contact) => {
    setSelectedContact(contact);
    setPhoneNumber(contact.phone || contact.mobile || '');
  };

  const dialpadButtons = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['*', '0', '#']
  ];

  const filteredContacts = contacts.filter(c => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      c.first_name?.toLowerCase().includes(search) ||
      c.last_name?.toLowerCase().includes(search) ||
      c.phone?.includes(search) ||
      c.email?.toLowerCase().includes(search)
    );
  });

  return (
    <Layout>
    <div className="p-6 h-[calc(100vh-64px)] flex flex-col gap-4">
      {/* Phase 2 Preview Banner */}
      <Alert className="bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800">
        <AlertTriangle className="h-5 w-5 text-amber-600" />
        <AlertDescription className="ml-2">
          <div className="flex items-center justify-between">
            <div>
              <span className="font-semibold text-amber-800 dark:text-amber-300">Phase 2 Preview Mode</span>
              <span className="text-amber-700 dark:text-amber-400 ml-2">
                Dialer UI is functional for preparation. Outbound calls will not connect until Active Mode is enabled.
              </span>
            </div>
            <Badge variant="outline" className="border-amber-500 text-amber-700 dark:text-amber-300">
              <Lock className="h-3 w-3 mr-1" />
              Safe Mode Active
            </Badge>
          </div>
        </AlertDescription>
      </Alert>

      <div className="flex-1 flex gap-6 min-h-0">
      {/* Left Panel - Dialer */}
      <div className="w-[350px] flex-shrink-0 flex flex-col gap-4">
        {/* Phone Display */}
        <Card>
          <CardContent className="pt-6">
            <div className="text-center mb-4">
              {isOnCall ? (
                <div className="space-y-2">
                  <div className="text-4xl font-mono font-bold text-green-500">
                    {formatDuration(callDuration)}
                  </div>
                  <div className="text-lg">{phoneNumber}</div>
                  <Badge variant="default" className="bg-green-500">On Call</Badge>
                </div>
              ) : (
                <Input
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="Enter phone number"
                  className="text-center text-2xl font-mono h-14"
                />
              )}
            </div>

            {/* Dialpad */}
            <div className="grid grid-cols-3 gap-2 mb-4">
              {dialpadButtons.flat().map((digit) => (
                <Button
                  key={digit}
                  variant="outline"
                  className="h-14 text-xl font-semibold"
                  onClick={() => handleDial(digit)}
                  disabled={isOnCall}
                >
                  {digit}
                </Button>
              ))}
            </div>

            {/* Call Controls */}
            <div className="flex gap-2 justify-center">
              {!isOnCall ? (
                <Button
                  size="lg"
                  className="flex-1 bg-green-500 hover:bg-green-600"
                  onClick={handleCall}
                >
                  <Phone className="h-5 w-5 mr-2" />
                  Call
                </Button>
              ) : (
                <>
                  <Button
                    variant={isMuted ? 'destructive' : 'outline'}
                    size="icon"
                    onClick={() => setIsMuted(!isMuted)}
                  >
                    {isMuted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
                  </Button>
                  <Button
                    variant={isHold ? 'secondary' : 'outline'}
                    size="icon"
                    onClick={() => setIsHold(!isHold)}
                  >
                    <Clock className="h-5 w-5" />
                  </Button>
                  <Button
                    size="lg"
                    variant="destructive"
                    className="flex-1"
                    onClick={handleHangup}
                  >
                    <PhoneOff className="h-5 w-5 mr-2" />
                    End Call
                  </Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Queue Selection */}
        <Card>
          <CardContent className="pt-4">
            <Label className="text-sm text-muted-foreground">Outbound Queue</Label>
            <Select value={selectedQueue} onValueChange={setSelectedQueue}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select queue" />
              </SelectTrigger>
              <SelectContent>
                {queues.filter(q => q.queue_type !== 'inbound').map(queue => (
                  <SelectItem key={queue.id} value={queue.id}>{queue.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Call Notes */}
        <Card className="flex-1">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Call Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add notes during call..."
              className="h-32 resize-none"
            />
            <Button variant="outline" size="sm" className="mt-2 w-full">
              <Send className="h-4 w-4 mr-2" />
              Save Notes
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Middle Panel - Contact/Script */}
      <div className="flex-1 flex flex-col gap-4 min-w-0">
        {/* Selected Contact Info */}
        {selectedContact && (
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold">{selectedContact.first_name} {selectedContact.last_name}</h3>
                  <p className="text-sm text-muted-foreground">{selectedContact.email}</p>
                  <p className="text-sm">{selectedContact.phone || selectedContact.mobile}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setSelectedContact(null)}>
                  Clear
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Script Display */}
        <Card className="flex-1">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Call Script
              </CardTitle>
              <Button variant="outline" size="sm" onClick={() => setShowScriptDialog(true)}>
                <Plus className="h-4 w-4 mr-1" />
                Select Script
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {selectedScript ? (
              <ScrollArea className="h-[400px] pr-4">
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-lg">{selectedScript.name}</h3>
                    <p className="text-sm text-muted-foreground">{selectedScript.description}</p>
                  </div>
                  <div className="bg-muted/50 p-4 rounded-lg whitespace-pre-wrap">
                    {selectedScript.content}
                  </div>
                  {selectedScript.objection_handlers && (
                    <div>
                      <h4 className="font-medium mb-2">Objection Handlers</h4>
                      <div className="space-y-2">
                        {Object.entries(selectedScript.objection_handlers).map(([objection, response]) => (
                          <div key={objection} className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded border-l-4 border-yellow-500">
                            <p className="font-medium text-sm">If: "{objection}"</p>
                            <p className="text-sm mt-1">{response}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </ScrollArea>
            ) : (
              <div className="h-[400px] flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No script selected</p>
                  <Button variant="link" onClick={() => setShowScriptDialog(true)}>
                    Select a script
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Right Panel - Contacts & History */}
      <div className="w-[300px] flex-shrink-0 flex flex-col gap-4">
        {/* Quick Contacts */}
        <Card className="flex-1">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Users className="h-4 w-4" />
              Contacts
            </CardTitle>
            <div className="relative mt-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search contacts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[200px]">
              <div className="space-y-1">
                {filteredContacts.slice(0, 20).map(contact => (
                  <button
                    key={contact.id}
                    onClick={() => selectContact(contact)}
                    className={`w-full text-left p-2 rounded hover:bg-accent transition-colors ${
                      selectedContact?.id === contact.id ? 'bg-accent' : ''
                    }`}
                  >
                    <div className="font-medium text-sm">{contact.first_name} {contact.last_name}</div>
                    <div className="text-xs text-muted-foreground">{contact.phone || contact.mobile}</div>
                  </button>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Recent Calls */}
        <Card className="flex-1">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <History className="h-4 w-4" />
              Recent Calls
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[200px]">
              <div className="space-y-1">
                {recentCalls.map(call => (
                  <button
                    key={call.id}
                    onClick={() => setPhoneNumber(call.caller_number)}
                    className="w-full text-left p-2 rounded hover:bg-accent transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm">{call.caller_name || call.caller_number}</span>
                      <Badge variant="outline" className="text-xs">
                        {call.duration_seconds ? `${Math.floor(call.duration_seconds / 60)}m` : '-'}
                      </Badge>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {new Date(call.created_at).toLocaleDateString()}
                    </div>
                  </button>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Script Selection Dialog */}
      <Dialog open={showScriptDialog} onOpenChange={setShowScriptDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Select Call Script</DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[400px] pr-4">
            <div className="space-y-2">
              {scripts.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No scripts available</p>
              ) : (
                scripts.map(script => (
                  <button
                    key={script.id}
                    onClick={() => {
                      setSelectedScript(script);
                      setShowScriptDialog(false);
                    }}
                    className={`w-full text-left p-4 rounded-lg border hover:border-primary transition-colors ${
                      selectedScript?.id === script.id ? 'border-primary bg-primary/5' : ''
                    }`}
                  >
                    <div className="font-medium">{script.name}</div>
                    <p className="text-sm text-muted-foreground line-clamp-2">{script.description}</p>
                    <div className="flex gap-2 mt-2">
                      <Badge variant="outline" className="text-xs">{script.script_type}</Badge>
                      {script.is_active && <Badge className="text-xs">Active</Badge>}
                    </div>
                  </button>
                ))
              )}
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowScriptDialog(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </div>
    </Layout>
  );
}
